import { z } from 'zod';

import { CashDenominationRowsSchema } from './cash-denominations.schema.js';

import {
  cashTreasuryDistributionRequestStatusValues,
  type CancelCashTreasuryDistributionRequestRequest,
  type CashTreasuryDistributionRequestDto,
  type CashTreasuryDistributionRequestStatus,
  type CreateCashTreasuryDistributionRequestRequest,
  type CreateCashTreasuryDistributionRequestResponse,
  type DeclineCashTreasuryDistributionRequestRequest,
  type GetCashTreasuryDistributionAvailabilityRequest,
  type GetCashTreasuryDistributionAvailabilityResponse,
  type GetCashTreasuryDistributionRequestRequest,
  type ListCashTreasuryDistributionRequestsRequest,
  type ListCashTreasuryDistributionRequestsResponse,
} from './cash-treasury-distribution-requests.dto.js';

const timestampSchema = z.iso.datetime();
const noteSchema = z.string().trim().min(1).max(500);
const idempotencyKeySchema = z.string().trim().min(1).max(120);

export const CashTreasuryDistributionRequestStatusSchema: z.ZodType<CashTreasuryDistributionRequestStatus> =
  z.enum(cashTreasuryDistributionRequestStatusValues);

export const CashTreasuryDistributionRequestDtoSchema: z.ZodType<CashTreasuryDistributionRequestDto> =
  z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    idempotencyKey: idempotencyKeySchema,
    treasuryCashLocationId: z.uuid(),
    targetCashLocationId: z.uuid(),
    targetStoreId: z.uuid(),
    targetLocationId: z.uuid(),
    targetStationId: z.uuid(),
    amountMinor: z.number().int().positive(),
    denominations: CashDenominationRowsSchema.nullable(),
    status: CashTreasuryDistributionRequestStatusSchema,
    requestedByUserId: z.uuid(),
    acceptedByUserId: z.uuid().nullable(),
    declinedByUserId: z.uuid().nullable(),
    cancelledByUserId: z.uuid().nullable(),
    requestedAt: timestampSchema,
    acceptedAt: timestampSchema.nullable(),
    declinedAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    expiredAt: timestampSchema.nullable(),
    expiresAt: timestampSchema.nullable(),
    requestNote: noteSchema.nullable(),
    declineReason: noteSchema.nullable(),
    cancellationReason: noteSchema.nullable(),
    ledgerAppliedAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const CreateCashTreasuryDistributionRequestRequestSchema: z.ZodType<CreateCashTreasuryDistributionRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      operationId: z.uuid(),
      occurredAt: timestampSchema.optional(),
      idempotencyKey: idempotencyKeySchema,
      treasuryCashLocationId: z.uuid(),
      targetCashLocationId: z.uuid(),
      targetStationId: z.uuid(),
      amountMinor: z.number().int().positive(),
      denominations: CashDenominationRowsSchema.nullable().optional(),
      note: noteSchema.nullable().optional(),
      expiresAt: timestampSchema.nullable().optional(),
    })
    .superRefine((value, context) => {
      if (
        value.denominations &&
        value.denominations.reduce((sum, row) => sum + row.denom * row.count, 0) !==
          value.amountMinor
      ) {
        context.addIssue({
          code: 'custom',
          path: ['denominations'],
          message: 'Treasury instruction denominations must equal the instruction amount.',
        });
      }
    })
    .strict();

export const CreateCashTreasuryDistributionRequestResponseSchema: z.ZodType<CreateCashTreasuryDistributionRequestResponse> =
  z.object({
    request: CashTreasuryDistributionRequestDtoSchema,
  });

export const GetCashTreasuryDistributionRequestRequestSchema: z.ZodType<GetCashTreasuryDistributionRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      cashTreasuryDistributionRequestId: z.uuid(),
    })
    .strict();

export const CancelCashTreasuryDistributionRequestRequestSchema: z.ZodType<CancelCashTreasuryDistributionRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      operationId: z.uuid(),
      occurredAt: timestampSchema.optional(),
      cashTreasuryDistributionRequestId: z.uuid(),
      reason: noteSchema.nullable().optional(),
    })
    .strict();

export const DeclineCashTreasuryDistributionRequestRequestSchema: z.ZodType<DeclineCashTreasuryDistributionRequestRequest> =
  z
    .object({
      companyId: z.uuid(),
      operationId: z.uuid(),
      treasuryInstructionId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      stationId: z.uuid(),
      reason: noteSchema,
      occurredAt: timestampSchema.optional(),
    })
    .strict();

export const ListCashTreasuryDistributionRequestsRequestSchema: z.ZodType<ListCashTreasuryDistributionRequestsRequest> =
  z
    .object({
      companyId: z.uuid(),
      treasuryCashLocationId: z.uuid().optional(),
      targetCashLocationId: z.uuid().optional(),
      targetStationId: z.uuid().optional(),
      status: CashTreasuryDistributionRequestStatusSchema.optional(),
      requestedFrom: timestampSchema.optional(),
      requestedTo: timestampSchema.optional(),
    })
    .strict();

export const ListCashTreasuryDistributionRequestsResponseSchema: z.ZodType<ListCashTreasuryDistributionRequestsResponse> =
  z.object({
    items: z.array(CashTreasuryDistributionRequestDtoSchema),
  });

export const GetCashTreasuryDistributionAvailabilityRequestSchema: z.ZodType<GetCashTreasuryDistributionAvailabilityRequest> =
  z
    .object({
      companyId: z.uuid(),
      treasuryCashLocationId: z.uuid(),
      targetStationId: z.uuid().optional(),
    })
    .strict();

export const GetCashTreasuryDistributionAvailabilityResponseSchema: z.ZodType<GetCashTreasuryDistributionAvailabilityResponse> =
  z.object({
    companyId: z.uuid(),
    treasuryCashLocationId: z.uuid(),
    targetStationId: z.uuid().nullable(),
    currentBalanceMinor: z.number().int(),
    reservedDistributionAmountMinor: z.number().int().nonnegative(),
    availableBalanceMinor: z.number().int(),
    cloudLedgerAsOf: timestampSchema,
    lastCloudCashMovementAt: timestampSchema.nullable(),
    targetStationLastUploadAt: timestampSchema.nullable(),
  });
